var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "nativewind/jsx-runtime";
import { HotUpdater } from '@hot-updater/react-native';
import { getAnalytics } from '@react-native-firebase/analytics';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import globalAxios from 'axios';
import { Provider, useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Platform, StatusBar, View } from 'react-native';
import Config from 'react-native-config';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { RootSiblingParent } from 'react-native-root-siblings';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { AppComponentsProvider } from '@/AppComponentsContext';
import { getStorageValue } from '@/atoms/atomForLocal';
import { accessTokenAtom } from '@/atoms/Auth';
import { useEnsureAnonymousUser } from '@/hooks/useEnsureAnonymousUser';
import ErrorBoundary from '@/components/ErrorBoundary';
import { LoadingView } from '@/components/LoadingView';
import { color } from '@/constant/color';
import SplashOverlay from '@/splash/SplashOverlay';
import { Configuration, DefaultApi, ToiletApi, } from '@/generated-sources/openapi';
import RootScreen from '@/screens/RootScreen';
import Logger from '@/logging/Logger';
import { startupTiming } from '@/logging/startupTiming';
import { readAppInjectedAuth } from '@/utils/appWebViewBridge';
import { logError, logRequest, logResponse } from '@/utils/DebugUtils';
import { setupGlobalFeatures } from '@/features/globalFeatures';
import './global.css';
setupGlobalFeatures();
const queryClient = new QueryClient();
// Get BASE_URL with local development override
const getBaseURL = () => {
    var _a, _b;
    // On web there is no localhost-emulator bridge; always use the configured
    // BASE_URL (the dev server origin must be CORS-allowed by that backend).
    if (Config.FLAVOR === 'local' && Platform.OS !== 'web') {
        // .env.local 의 BASE_URL 을 그대로 존중한다. 예전엔 8080 을 하드코딩해서, 포트를
        // 바꿔 띄울 때(8080 점유 등)마다 이 파일을 임시로 고쳤다 되돌려야 했다.
        const configured = (_a = Config.BASE_URL) !== null && _a !== void 0 ? _a : 'http://localhost:8080';
        // 안드로이드 에뮬레이터에서 localhost 는 호스트가 아니라 에뮬레이터 자신을 가리킨다.
        return Platform.OS === 'android'
            ? configured.replace('localhost', '10.0.2.2')
            : configured;
    }
    // 앱 웹뷰 안이라면 앱이 주입한 BASE_URL 을 최우선으로 쓴다.
    // web 번들에는 빌드 시점의 BASE_URL(prod)이 baked 되어 있어서, sandbox 앱 웹뷰에서
    // 그대로 쓰면 **dev 토큰으로 prod API 를 호출**해 전부 401 이 난다(좋아요/저장 등).
    // web/config/api.ts 의 apiConfig 는 BbucleRoadScreen 이 주입 baseUrl 로 덮지만,
    // 이 Configuration(useAppComponents 용)은 아무도 덮지 않아 두 인스턴스가 엇갈렸다.
    // native 는 readAppInjectedAuth() 가 항상 null 이라 영향 없다.
    const injectedBaseUrl = (_b = readAppInjectedAuth()) === null || _b === void 0 ? void 0 : _b.baseUrl;
    if (injectedBaseUrl) {
        return injectedBaseUrl;
    }
    return Config.BASE_URL;
};
const AppWithProviders = () => {
    return (_jsx(View, { style: { flex: 1, backgroundColor: color.brand30 }, children: _jsx(Provider, { children: _jsx(SafeAreaProvider, { children: _jsx(AppComponentsProvider, { api: new DefaultApi(new Configuration({ basePath: getBaseURL() })), toiletApi: new ToiletApi(new Configuration({ basePath: getBaseURL() })), children: _jsx(QueryClientProvider, { client: queryClient, children: _jsx(ErrorBoundary, { children: _jsx(App, {}) }) }) }) }) }) }));
};
const App = () => {
    const accessToken = useAtomValue(accessTokenAtom);
    const setAccessToken = useSetAtom(accessTokenAtom);
    const ensureAnonymousUser = useEnsureAnonymousUser();
    // web 무로그인화: 토큰이 없으면 익명 계정을 발행해 항상 토큰이 존재하게 한다.
    // 토큰이 첫 화면의 조회 쿼리(전역 Anonymous라 무토큰이면 401)보다 먼저 존재하도록
    // 발행이 끝날 때까지 RootScreen 렌더를 막는다(스피너). 실패해도(finally) 앱은 띄운다.
    // 이미 토큰이 있으면(재방문 — atom getOnInit로 동기 hydrate) 스피너 없이 바로 렌더.
    const [webBootstrapDone, setWebBootstrapDone] = useState(Platform.OS !== 'web' || !!accessToken);
    // 표면(app/web/app_webview) 디멘션을 setUserId 보다 먼저 심는다 — 신원이 붙기 전에
    // 나가는 이벤트도 표면으로 나눌 수 있어야 한다.
    useEffect(() => {
        Logger.setSurface();
    }, []);
    useEffect(() => {
        if (Platform.OS !== 'web' || accessToken) {
            setWebBootstrapDone(true);
            return;
        }
        let cancelled = false;
        ensureAnonymousUser()
            .catch(e => logError(e, 'web anonymous bootstrap'))
            .finally(() => {
            if (!cancelled) {
                setWebBootstrapDone(true);
            }
        });
        return () => {
            cancelled = true;
        };
        // 마운트 시 1회만. 이후 토큰 발행은 setAccessToken → 재렌더로 반영된다.
    }, []);
    useEffect(() => {
        // Request logging interceptor.
        // 토큰은 매 요청 시점에 storage에서 직접 읽는다 — atom state 클로저를 캡처하면
        // 익명 부트스트랩 직후 토큰이 아직 인터셉터에 반영 안 된 채 쿼리가 나가 401이 날 수 있다.
        const requestInterceptorId = globalAxios.interceptors.request.use((config) => __awaiter(void 0, void 0, void 0, function* () {
            logRequest(config);
            config.headers.set('Content-Type', 'application/json');
            config.headers.set('Accept', 'application/json');
            const token = getStorageValue('scc-token');
            if (token && !config.headers.get('Authorization')) {
                // 회원가입 시나리오에서 state에 저장된 것과는 다른 access token을 사용하고 싶은 경우가 있다.
                // 따라서 이미 Authoirzation header가 지정된 상태라면 access token injection을 스킵한다.
                config.headers.set('Authorization', `Bearer ${token}`);
            }
            return config;
        }), error => {
            logError(error, 'Request Interceptor');
            return Promise.reject(error);
        });
        // Response logging interceptor
        const responseInterceptorId = globalAxios.interceptors.response.use(response => {
            logResponse(response);
            return response;
        }, (error) => {
            logError(error, 'Response Interceptor');
            if (error.response && error.response.status === 401) {
                setAccessToken(null);
            }
            return Promise.reject(error);
        });
        return () => {
            globalAxios.interceptors.request.eject(requestInterceptorId);
            globalAxios.interceptors.response.eject(responseInterceptorId);
        };
    }, [setAccessToken]);
    if (!webBootstrapDone) {
        return (_jsx(View, { style: {
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: color.white,
            }, children: _jsx(ActivityIndicator, { size: "large", color: color.brand50 }) }));
    }
    return (_jsx(GestureHandlerRootView, { style: { flex: 1, backgroundColor: color.white }, children: _jsxs(RootSiblingParent, { children: [_jsx(StatusBar, { barStyle: 'dark-content', backgroundColor: color.white }), _jsx(RootScreen, {}), _jsx(LoadingView, {})] }) }));
};
const AppWithMigration = () => {
    return _jsx(AppWithProviders, {});
};
const HotUpdatedApp = HotUpdater.wrap({
    source: (_a = Config.HOT_UPDATER_URL) !== null && _a !== void 0 ? _a : '',
    fallbackComponent: () => null,
    onProgress: () => {
        // 번들 다운로드(UPDATING) 시작 시점만 한 번 기록 — 다운로드 소요 분리용
        if (!startupTiming.otaFirstProgress) {
            startupTiming.otaFirstProgress = Date.now();
        }
    },
    onUpdateProcessCompleted: res => {
        var _a, _b;
        startupTiming.otaCompleted = Date.now();
        Logger.setOtaBundleId((_a = HotUpdater.getBundleId()) !== null && _a !== void 0 ? _a : 'unknown');
        Logger.logOtaCompleted({
            status: res.status,
            didDownload: res.status !== 'UP_TO_DATE',
            jsToOtaMs: startupTiming.otaCompleted - startupTiming.jsStart,
            downloadMs: startupTiming.otaFirstProgress
                ? startupTiming.otaCompleted - startupTiming.otaFirstProgress
                : 0,
            bundleId: (_b = HotUpdater.getBundleId()) !== null && _b !== void 0 ? _b : 'unknown',
        });
    },
    onError: error => {
        getAnalytics().logEvent('HotUpdaterError', {
            error: error.message,
        });
    },
})(AppWithMigration);
const AppRoot = () => (_jsx(View, { style: { flex: 1, backgroundColor: color.brand30 }, children: Platform.OS === 'web' ? (
    // Web: no OTA (HotUpdater) and no native splash. The provider tree is shared.
    _jsx(AppWithProviders, {})) : (_jsxs(_Fragment, { children: [_jsx(HotUpdatedApp, {}), _jsx(SplashOverlay, {})] })) }));
export default AppRoot;
